<template>
  <div v-if="showRecommendedOffers">
    <div class="mt-3">
      <h3 class="offers-title">
        {{ t("RECOMMENDED_OFFERS") }}
      </h3>
      <p>
        {{ t("RECOMMENDED_OFFERS_SUBTITLE") }}
      </p>
    </div>

    <table>
      <thead>
        <tr>
          <th>{{ t("DEVICE") }}</th>
          <th>{{ t("PRICE") }}</th>
          <th>{{ t("DOWN_PAYMENT") }}</th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(offer, index) in recommendedOffers"
          :class="handleSelectedOffer(index)"
          :key="index"
          @click="selectRecommendedOffer(index)"
        >
          <td>
            <div>{{ offer.modelSelect }}</div>
            <div>
              {{ handleShowDescription(offer.description) }}
            </div>
          </td>
          <td>
            {{ handlePriceFormat(offer.retailPrice) }}
          </td>
          <td>
            <div class="deposit-cell">
              {{ handlePriceFormat(offer.minDownPayment) }}
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watchEffect } from "vue";
import { useStore } from "vuex";
import { useI18n } from "@/apps/merchant3/composables/useI18n";

import EventLogs from "@/apps/merchant3/types/EventLogs";
import logEvent from "@/apps/merchant3/services/event-logs.service";

import formatPrice from "@/apps/merchant3/shared-helpers/price-formatter.helper";
import useCartPersistState from "@/apps/merchant3/hooks/useCartPersistState";
import useCurrency from "@/apps/merchant3/shared-hooks/useCurrency";
import useMerchantPersistState from "@/apps/merchant3/hooks/useMerchantPersistState";

const props = defineProps({
  selectedRecommendedOffer: Number,
});

const emit = defineEmits(["recommended-offer"]);

const { t } = useI18n();
const store = useStore();

const RECOMM_VERSION = "V2";
const recommendedOffers = ref();
const selectedOffer = ref(props.selectedRecommendedOffer);
const showRecommendedOffers = computed(() => {
  return store.state.carts?.experiments?.recommendedOffersV2 ?? false;
});

const selectRecommendedOffer = (index: number) => {
  if (selectedOffer.value === index) return;

  const manufacturerSelected = recommendedOffers.value[index].manufacturer;
  const deviceModelIdSelected = recommendedOffers.value[index].deviceModelId;
  const retailPriceSelected = recommendedOffers.value[index].retailPrice;
  const minDownPaymentSelected = recommendedOffers.value[index].minDownPayment;
  const descriptionSelected = recommendedOffers.value[index].description;
  recommendedOffers.value[index].version = RECOMM_VERSION;

  const recommendedOfferSelected = {
    manufacturer: manufacturerSelected,
    deviceModelId: deviceModelIdSelected,
    retailPrice: retailPriceSelected,
    minDownPayment: minDownPaymentSelected,
    description: descriptionSelected,
    selectedRow: index,
  };
  logRecommendedOffersClicked(index);
  emit("recommended-offer", recommendedOfferSelected);
};

const logRecommendedOffersClicked = (index: number) => {
  const data = { offer: recommendedOffers.value[index] };
  logEvent(EventLogs.Events.RECOM_OFFERS_CLICKED, data);
};

const { getCurrency } = useCurrency();
const currency = computed(() => {
  return getCurrency(store.state.merchant?.merchant?.country);
});

const handlePriceFormat = (value: string): string => {
  return `${currency.value?.sign}${formatPrice(value)}`;
};

const handleShowDescription = (value: string): string => {
  return value ? `(${value})` : "";
};

const handleSelectedOffer = (index: number) => {
  return selectedOffer.value === index ? "selected-offer" : "";
};

onMounted(() => {
  useMerchantPersistState(() => true);
  useCartPersistState(() => true);
  let timer = 0;
  const interval = setInterval(() => {
    timer++;
    if (store.state.offers.recommendedOffers?.length) {
      clearInterval(interval);
      recommendedOffers.value = store.state.offers.recommendedOffers;
      logRecommendedOffersDisplayed();
    }
    if (timer > 7) {
      clearInterval(interval);
    }
  }, 1000);
});

const logRecommendedOffersDisplayed = () => {
  const data = {
    offer: recommendedOffers.value,
    version: RECOMM_VERSION,
  };
  logEvent(EventLogs.Events.RECOM_OFFERS_DISPLAYED, data);
};

watchEffect(() => {
  if (props.selectedRecommendedOffer !== undefined) {
    selectedOffer.value = props.selectedRecommendedOffer;
  }
});
</script>
<style lang="scss" scoped>
@import "@/assets/scss/_constants.scss";

.offers-title {
  color: $pj-color-zomp;
}

table {
  width: 100%;
  empty-cells: show;
  margin-bottom: 50px;
  box-shadow: rgb(204, 204, 204) 0 0 0 1px;
}

thead tr {
  padding: 6px;
  text-align: left;
  height: 40px;
  color: $pj-color-green;
  box-shadow: rgb(204, 204, 204) 0 0 0 1px;
}

th {
  font-size: 14px;
  font-weight: bold;
  padding-left: 8px;
}
tr {
  border: 1px solid $pj-color-light-gray;
  height: 40px;
}

tbody tr {
  cursor: pointer;
}

tbody tr:hover {
  background-color: $pj-color-green-row-hover;
  border-style: double;
  border-color: $pj-color-green;
}

td {
  padding-left: 8px;
}

.deposit-cell {
  color: $pj-color-green-cell-text;
  font-weight: bold;
  background-color: $pj-color-green-cell;
  border-radius: 5px;
  display: inline-block;
  padding: 0.25em 1em;
}

.selected-offer,
.selected-offer:hover {
  font-weight: bold;
  cursor: default;
  border: 1px solid $pj-color-green-hover;
  background-color: $pj-color-green-hover;
  box-shadow: rgba(60, 64, 67, 0.3) 0 1px 2px 0,
    rgba(60, 64, 67, 0.15) 0 2px 6px 2px;
}
</style>
